import { ICountryData } from "../../../../services/api";

/**
 * Gets the country name from a country ID
 * @param countryId - The country ID to look up
 * @param countries - Array of country data
 * @returns The country name or undefined if not found
 */
export const getCountryNameFromId = (
  countryId: string,
  countries: ICountryData[]
): string | undefined => {
  if (!countryId || !countries.length) return undefined;
  const country = countries.find((c) => c._id === countryId);
  return country?.country_name;
};

/**
 * Maps country names to correct ISO 3166-1 alpha-2 codes for flag display
 * @param countryName - The country name to map
 * @returns The ISO code or null if not found
 */
export const getFlagCodeFromCountryName = (
  countryName: string | undefined
): string | null => {
  if (!countryName) return null;

  const normalizedName = countryName.trim().toLowerCase();

  // Map country names to correct ISO 3166-1 alpha-2 codes for flags
  const countryNameToFlagCodeMap: Record<string, string> = {
    austria: "AT",
    belgium: "BE",
    "bosnia-herzegovina": "BA",
    botswana: "BW",
    cyprus: "CY",
    ghana: "GH",
    iceland: "IS",
    ireland: "IE",
    kosovo: "XK",
    lesotho: "LS",
    malta: "MT",
    montenegro: "ME",
    namibia: "NA",
    "south africa": "ZA",
    swaziland: "SZ",
    eswatini: "SZ",
    switzerland: "CH",
    "united kingdom": "GB",
    uk: "GB",
    "great britain": "GB",
  };

  return countryNameToFlagCodeMap[normalizedName] || null;
};

/**
 * Normalizes a country name by removing "The" prefix if present
 * @param countryName - The country name to normalize
 * @returns The normalized country name
 */
export const normalizeCountryName = (countryName: string): string => {
  let normalized = countryName.trim();
  if (normalized.toLowerCase().startsWith("the ")) {
    normalized = normalized.substring(4).trim();
  }
  return normalized;
};

/**
 * Maps common country name variations to standard names
 * @param countryName - The country name to map
 * @returns The mapped country name or the original if no mapping exists
 */
export const mapCountryVariation = (countryName: string): string => {
  const countryVariations: Record<string, string> = {
    UK: "United Kingdom",
    "Great Britain": "United Kingdom",
    GB: "United Kingdom",
    "The Netherlands": "Netherlands",
    "The United Kingdom": "United Kingdom",
  };

  return countryVariations[countryName] || countryName;
};

/**
 * Finds a country in the countries array by name (with normalization and variation mapping)
 * @param countryName - The country name to find
 * @param countries - Array of country data
 * @returns The found country or undefined
 */
export const findCountryByName = (
  countryName: string,
  countries: ICountryData[]
): ICountryData | undefined => {
  if (!countryName || !countries.length) return undefined;

  // Normalize the country name
  let normalizedName = normalizeCountryName(countryName);

  // Try exact match first
  let selectedCountry = countries.find(
    (country: ICountryData) =>
      country.country_name?.toLowerCase() === normalizedName.toLowerCase()
  );

  // If no exact match, try with variation mapping
  if (!selectedCountry) {
    const mappedCountry = mapCountryVariation(countryName) || mapCountryVariation(normalizedName);
    if (mappedCountry !== countryName && mappedCountry !== normalizedName) {
      selectedCountry = countries.find(
        (country: ICountryData) =>
          country.country_name?.toLowerCase() === mappedCountry.toLowerCase()
      );
    }
  }

  return selectedCountry;
};

/**
 * Finds the default country (India) in the countries array.
 * Accepts alpha-2 (`IN`) or alpha-3 (`IND`) and common name spellings.
 */
export const findDefaultCountry = (
  countries: ICountryData[]
): ICountryData | undefined => {
  const code = (c: ICountryData) => (c.country_code || "").toUpperCase();
  return countries.find(
    (country: ICountryData) =>
      country.country_name?.toLowerCase() === "india" ||
      code(country) === "IN" ||
      code(country) === "IND"
  );
};
